import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { 
  ArrowLeft, 
  DollarSign, 
  Calculator,
  Plus,
  Trash2,
  FileText,
  Calendar,
  CreditCard,
  AlertCircle,
  TrendingUp,
  Edit,
  Building,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import TourOperatorSelect from './TourOperatorSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TripAdmin = () => {
  const { tripId } = useParams();
  const { user } = useAuth();
  const [tripData, setTripData] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [supplierCosts, setSupplierCosts] = useState([]);
  const [supplierPayments, setSupplierPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateAdminDialog, setShowCreateAdminDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showSupplierCostDialog, setShowSupplierCostDialog] = useState(false);
  const [showSupplierPaymentDialog, setShowSupplierPaymentDialog] = useState(false);
  
  const [newAdminData, setNewAdminData] = useState({
    practice_number: '',
    booking_number: '',
    gross_amount: '',
    net_amount: '',
    discount: '0',
    practice_confirm_date: '',
    client_departure_date: '',
    confirmation_deposit: '0',
    balance_due_date: '',
    tour_operator_id: null
  });

  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_date: '',
    payment_type: 'installment',
    collected_by: 'agent',
    notes: ''
  });

  const [editingPayment, setEditingPayment] = useState(null);
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);

  const [newSupplierCost, setNewSupplierCost] = useState({
    supplier_name: '',
    tour_operator_id: null,
    description: '',
    amount: '',
    cost_date: '',
    category: 'accommodation',
    notes: ''
  });

  const [editingSupplierCost, setEditingSupplierCost] = useState(null);
  const [showEditSupplierCostDialog, setShowEditSupplierCostDialog] = useState(false);

  const [newSupplierPayment, setNewSupplierPayment] = useState({
    supplier_name: '',
    tour_operator_id: null,
    amount: '',
    payment_date: '',
    bank_reference: '',
    payment_method: 'bank_transfer',
    notes: ''
  });

  const [editingSupplierPayment, setEditingSupplierPayment] = useState(null);
  const [showEditSupplierPaymentDialog, setShowEditSupplierPaymentDialog] = useState(false);

  useEffect(() => {
    if (tripId) {
      fetchTripAdminData();
    }
  }, [tripId]);

  const fetchTripAdminData = async () => {
    try {
      const [tripRes, adminRes] = await Promise.all([
        axios.get(`${API}/trips/${tripId}/full`),
        axios.get(`${API}/trips/${tripId}/admin`)
      ]);

      setTripData(tripRes.data);
      setAdminData(adminRes.data);

      if (adminRes.data) {
        const [paymentsRes, costsRes, supplierPaymentsRes] = await Promise.all([
          axios.get(`${API}/trip-admin/${adminRes.data.id}/payments`),
          axios.get(`${API}/trip-admin/${adminRes.data.id}/supplier-costs`),
          axios.get(`${API}/trip-admin/${adminRes.data.id}/supplier-payments`)
        ]);
        setPayments(paymentsRes.data);
        setSupplierCosts(costsRes.data);
        setSupplierPayments(supplierPaymentsRes.data);
      }
    } catch (error) {
      console.error('Error fetching trip admin data:', error);
      toast.error('Errore nel caricamento dei dati amministrativi');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    try {
      // Validate required fields
      if (!newAdminData.practice_number || !newAdminData.booking_number || 
          !newAdminData.gross_amount || !newAdminData.net_amount ||
          !newAdminData.practice_confirm_date || !newAdminData.client_departure_date) {
        toast.error('Compila tutti i campi obbligatori');
        return;
      }

      const adminData = {
        ...newAdminData,
        trip_id: tripId,
        gross_amount: parseFloat(newAdminData.gross_amount),
        net_amount: parseFloat(newAdminData.net_amount),
        discount: parseFloat(newAdminData.discount || 0),
        confirmation_deposit: parseFloat(newAdminData.confirmation_deposit || 0),
        practice_confirm_date: new Date(newAdminData.practice_confirm_date).toISOString(),
        client_departure_date: new Date(newAdminData.client_departure_date).toISOString(),
        balance_due_date: newAdminData.balance_due_date ? new Date(newAdminData.balance_due_date).toISOString() : null
      };

      await axios.post(`${API}/trips/${tripId}/admin`, adminData);
      toast.success('Dati amministrativi creati con successo!');
      setShowCreateAdminDialog(false);
      setNewAdminData({
        practice_number: '',
        booking_number: '',
        gross_amount: '',
        net_amount: '',
        discount: '0',
        practice_confirm_date: '',
        client_departure_date: '',
        confirmation_deposit: '0',
        balance_due_date: ''
      });
      fetchTripAdminData();
    } catch (error) {
      console.error('Error creating admin data:', error);
      const errorMessage = error.response?.data?.detail || 'Errore nella creazione dei dati amministrativi';
      toast.error(errorMessage);
    }
  };

  const handleCreatePayment = async () => {
    try {
      // Validate required fields
      if (!newPayment.amount || !newPayment.payment_date) {
        toast.error('Compila tutti i campi obbligatori');
        return;
      }

      // Prepare payment data with correct types
      const paymentData = {
        trip_admin_id: adminData.id,
        amount: parseFloat(newPayment.amount),
        payment_date: new Date(newPayment.payment_date).toISOString(),
        payment_type: newPayment.payment_type || 'installment',
        collected_by: newPayment.collected_by || 'agent',
        notes: newPayment.notes || ''
      };

      await axios.post(`${API}/trip-admin/${adminData.id}/payments`, paymentData);
      toast.success('Pagamento registrato con successo!');
      setShowPaymentDialog(false);
      setNewPayment({
        amount: '',
        payment_date: '',
        payment_type: 'installment',
        collected_by: 'agent',
        notes: ''
      });
      fetchTripAdminData();
    } catch (error) {
      console.error('Error creating payment:', error);
      const errorMessage = error.response?.data?.detail || 'Errore nella registrazione del pagamento';
      toast.error(errorMessage);
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment({
      ...payment,
      payment_date: payment.payment_date ? new Date(payment.payment_date).toISOString().slice(0, 16) : ''
    });
    setShowEditPaymentDialog(true);
  };

  const handleUpdatePayment = async () => {
    try {
      if (!editingPayment.amount || !editingPayment.payment_date) {
        toast.error('Compila tutti i campi obbligatori');
        return;
      }

      const updateData = {
        amount: parseFloat(editingPayment.amount),
        payment_date: new Date(editingPayment.payment_date).toISOString(),
        payment_type: editingPayment.payment_type,
        collected_by: editingPayment.collected_by || 'agent',
        notes: editingPayment.notes || ''
      };

      await axios.put(`${API}/payments/${editingPayment.id}`, updateData);
      toast.success('Pagamento aggiornato con successo!');
      setShowEditPaymentDialog(false);
      setEditingPayment(null);
      fetchTripAdminData();
    } catch (error) {
      console.error('Error updating payment:', error);
      toast.error('Errore nell\'aggiornamento del pagamento');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo pagamento?')) {
      try {
        await axios.delete(`${API}/payments/${paymentId}`);
        toast.success('Pagamento eliminato con successo!');
        fetchTripAdminData();
      } catch (error) {
        console.error('Error deleting payment:', error);
        toast.error('Errore nell\'eliminazione del pagamento');
      }
    }
  };

  const getCollectedByLabel = (collectedBy) => {
    switch (collectedBy) {
      case 'agent': return 'Agente';
      case 'supplier': return 'Fornitore';
      default: return 'Agente';
    }
  };

  const getCostCategoryLabel = (category) => {
    switch (category) {
      case 'accommodation': return 'Alloggio';
      case 'transport': return 'Trasporto';
      case 'excursion': return 'Escursione';
      case 'insurance': return 'Assicurazione';
      case 'other': return 'Altro';
      default: return category;
    }
  };

  // Supplier Costs handlers
  const handleCreateSupplierCost = async () => {
    try {
      if (!newSupplierCost.supplier_name || !newSupplierCost.amount || !newSupplierCost.cost_date) {
        toast.error('Compila tutti i campi obbligatori');
        return;
      }

      const costData = {
        trip_admin_id: adminData.id,
        supplier_name: newSupplierCost.supplier_name,
        tour_operator_id: newSupplierCost.tour_operator_id || null,
        description: newSupplierCost.description,
        amount: parseFloat(newSupplierCost.amount),
        cost_date: new Date(newSupplierCost.cost_date).toISOString(),
        category: newSupplierCost.category,
        notes: newSupplierCost.notes
      };

      await axios.post(`${API}/trip-admin/${adminData.id}/supplier-costs`, costData);
      toast.success('Costo fornitore registrato!');
      setShowSupplierCostDialog(false);
      setNewSupplierCost({ supplier_name: '', tour_operator_id: null, description: '', amount: '', cost_date: '', category: 'accommodation', notes: '' });
      fetchTripAdminData();
    } catch (error) {
      console.error('Error creating supplier cost:', error);
      toast.error('Errore nella registrazione del costo');
    }
  };

  const handleEditSupplierCost = (cost) => {
    setEditingSupplierCost({
      ...cost,
      cost_date: cost.cost_date ? new Date(cost.cost_date).toISOString().slice(0, 16) : ''
    });
    setShowEditSupplierCostDialog(true);
  };

  const handleUpdateSupplierCost = async () => {
    try {
      const updateData = {
        supplier_name: editingSupplierCost.supplier_name,
        tour_operator_id: editingSupplierCost.tour_operator_id || null,
        description: editingSupplierCost.description,
        amount: parseFloat(editingSupplierCost.amount),
        cost_date: new Date(editingSupplierCost.cost_date).toISOString(),
        category: editingSupplierCost.category,
        notes: editingSupplierCost.notes
      };

      await axios.put(`${API}/supplier-costs/${editingSupplierCost.id}`, updateData);
      toast.success('Costo aggiornato!');
      setShowEditSupplierCostDialog(false);
      setEditingSupplierCost(null);
      fetchTripAdminData();
    } catch (error) {
      console.error('Error updating supplier cost:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDeleteSupplierCost = async (costId) => {
    if (window.confirm('Eliminare questo costo?')) {
      try {
        await axios.delete(`${API}/supplier-costs/${costId}`);
        toast.success('Costo eliminato!');
        fetchTripAdminData();
      } catch (error) {
        toast.error('Errore nell\'eliminazione');
      }
    }
  };

  // Supplier Payments handlers
  const handleCreateSupplierPayment = async () => {
    try {
      if (!newSupplierPayment.supplier_name || !newSupplierPayment.amount || !newSupplierPayment.payment_date) {
        toast.error('Compila tutti i campi obbligatori');
        return;
      }

      const paymentData = {
        trip_admin_id: adminData.id,
        supplier_name: newSupplierPayment.supplier_name,
        tour_operator_id: newSupplierPayment.tour_operator_id || null,
        amount: parseFloat(newSupplierPayment.amount),
        payment_date: new Date(newSupplierPayment.payment_date).toISOString(),
        bank_reference: newSupplierPayment.bank_reference,
        payment_method: newSupplierPayment.payment_method,
        notes: newSupplierPayment.notes
      };

      await axios.post(`${API}/trip-admin/${adminData.id}/supplier-payments`, paymentData);
      toast.success('Bonifico registrato!');
      setShowSupplierPaymentDialog(false);
      setNewSupplierPayment({ supplier_name: '', tour_operator_id: null, amount: '', payment_date: '', bank_reference: '', payment_method: 'bank_transfer', notes: '' });
      fetchTripAdminData();
    } catch (error) {
      console.error('Error creating supplier payment:', error);
      toast.error('Errore nella registrazione del bonifico');
    }
  };

  const handleEditSupplierPayment = (payment) => {
    setEditingSupplierPayment({
      ...payment,
      payment_date: payment.payment_date ? new Date(payment.payment_date).toISOString().slice(0, 16) : ''
    });
    setShowEditSupplierPaymentDialog(true);
  };

  const handleUpdateSupplierPayment = async () => {
    try {
      const updateData = {
        supplier_name: editingSupplierPayment.supplier_name,
        tour_operator_id: editingSupplierPayment.tour_operator_id || null,
        amount: parseFloat(editingSupplierPayment.amount),
        payment_date: new Date(editingSupplierPayment.payment_date).toISOString(),
        bank_reference: editingSupplierPayment.bank_reference,
        payment_method: editingSupplierPayment.payment_method,
        notes: editingSupplierPayment.notes
      };

      await axios.put(`${API}/supplier-payments/${editingSupplierPayment.id}`, updateData);
      toast.success('Bonifico aggiornato!');
      setShowEditSupplierPaymentDialog(false);
      setEditingSupplierPayment(null);
      fetchTripAdminData();
    } catch (error) {
      console.error('Error updating supplier payment:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDeleteSupplierPayment = async (paymentId) => {
    if (window.confirm('Eliminare questo bonifico?')) {
      try {
        await axios.delete(`${API}/supplier-payments/${paymentId}`);
        toast.success('Bonifico eliminato!');
        fetchTripAdminData();
      } catch (error) {
        toast.error('Errore nell\'eliminazione');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'paid': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!tripData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Viaggio non trovato</h2>
          <Link to="/manage-trips">
            <Button>Torna ai Viaggi</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to={`/trips/${tripId}`}>
                <Button variant="ghost" size="sm" className="mr-4">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              </Link>
              <DollarSign className="h-8 w-8 text-teal-600 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-slate-800">Amministrazione Finanziaria</h1>
                <p className="text-sm text-slate-600">{tripData.trip.title}</p>
              </div>
            </div>
            
            {!adminData && (
              <Dialog open={showCreateAdminDialog} onOpenChange={setShowCreateAdminDialog}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Crea Scheda Finanziaria
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Crea Scheda Finanziaria</DialogTitle>
                    <DialogDescription>
                      Inserisci i dati amministrativi e finanziari del viaggio
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="practice_number">Numero Pratica</Label>
                      <Input
                        id="practice_number"
                        value={newAdminData.practice_number}
                        onChange={(e) => setNewAdminData({...newAdminData, practice_number: e.target.value})}
                        placeholder="PR001"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="booking_number">Numero Prenotazione</Label>
                      <Input
                        id="booking_number"
                        value={newAdminData.booking_number}
                        onChange={(e) => setNewAdminData({...newAdminData, booking_number: e.target.value})}
                        placeholder="BK001"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="gross_amount">Importo Lordo (€)</Label>
                      <Input
                        id="gross_amount"
                        type="number"
                        step="0.01"
                        value={newAdminData.gross_amount}
                        onChange={(e) => setNewAdminData({...newAdminData, gross_amount: e.target.value})}
                        placeholder="1500.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="net_amount">Importo Netto (€)</Label>
                      <Input
                        id="net_amount"
                        type="number"
                        step="0.01"
                        value={newAdminData.net_amount}
                        onChange={(e) => setNewAdminData({...newAdminData, net_amount: e.target.value})}
                        placeholder="1200.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="discount">Sconto (€)</Label>
                      <Input
                        id="discount"
                        type="number"
                        step="0.01"
                        value={newAdminData.discount}
                        onChange={(e) => setNewAdminData({...newAdminData, discount: e.target.value})}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmation_deposit">Acconto Conferma (€)</Label>
                      <Input
                        id="confirmation_deposit"
                        type="number"
                        step="0.01"
                        value={newAdminData.confirmation_deposit}
                        onChange={(e) => setNewAdminData({...newAdminData, confirmation_deposit: e.target.value})}
                        placeholder="300.00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="practice_confirm_date">Data Conferma Pratica</Label>
                      <Input
                        id="practice_confirm_date"
                        type="datetime-local"
                        value={newAdminData.practice_confirm_date}
                        onChange={(e) => setNewAdminData({...newAdminData, practice_confirm_date: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="client_departure_date">Data Partenza Cliente</Label>
                      <Input
                        id="client_departure_date"
                        type="datetime-local"
                        value={newAdminData.client_departure_date}
                        onChange={(e) => setNewAdminData({...newAdminData, client_departure_date: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="balance_due_date">Data Scadenza Saldo (opzionale)</Label>
                      <Input
                        id="balance_due_date"
                        type="date"
                        value={newAdminData.balance_due_date}
                        onChange={(e) => setNewAdminData({...newAdminData, balance_due_date: e.target.value})}
                      />
                      <p className="text-xs text-slate-500">
                        Imposta la data per ricevere alert 3 giorni prima della scadenza
                      </p>
                    </div>

                    <div className="col-span-2">
                      <TourOperatorSelect
                        value={newAdminData.tour_operator_id}
                        onChange={(id) => setNewAdminData({...newAdminData, tour_operator_id: id})}
                        label="Tour Operator (Rubrica) — opzionale"
                        testId="practice-tour-operator-select"
                      />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreateAdminDialog(false)}>
                      Annulla
                    </Button>
                    <Button onClick={handleCreateAdmin}>
                      Crea Scheda
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {adminData ? (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Panoramica</span>
              </TabsTrigger>
              <TabsTrigger value="payments" className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                <span className="hidden sm:inline">Incassi</span> ({payments.length})
              </TabsTrigger>
              <TabsTrigger value="supplier-costs" className="flex items-center gap-2">
                <Building className="w-4 h-4" />
                <span className="hidden sm:inline">Costi TO</span> ({supplierCosts.length})
              </TabsTrigger>
              <TabsTrigger value="supplier-payments" className="flex items-center gap-2">
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">Bonifici</span> ({supplierPayments.length})
              </TabsTrigger>
              <TabsTrigger value="analytics" className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline">Analytics</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              {/* Financial Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-green-100">
                      Importo Lordo
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(adminData.gross_amount)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-blue-100">
                      Commissione Agente
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(adminData.agent_commission)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-purple-100">
                      Saldo Dovuto
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {formatCurrency(adminData.balance_due)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-orange-100">
                      Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-lg font-bold">
                      {adminData.status.toUpperCase()}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle>Informazioni Pratica</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-slate-600">Numero Pratica</Label>
                        <p className="font-semibold">{adminData.practice_number}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-600">Numero Prenotazione</Label>
                        <p className="font-semibold">{adminData.booking_number}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-600">Data Conferma</Label>
                        <p>{new Date(adminData.practice_confirm_date).toLocaleDateString('it-IT')}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-600">Data Partenza</Label>
                        <p>{new Date(adminData.client_departure_date).toLocaleDateString('it-IT')}</p>
                      </div>
                      {adminData.balance_due_date && (
                        <div className="col-span-2">
                          <Label className="text-sm font-medium text-slate-600">Data Scadenza Saldo</Label>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold">
                              {new Date(adminData.balance_due_date).toLocaleDateString('it-IT')}
                            </p>
                            {(() => {
                              const today = new Date();
                              const dueDate = new Date(adminData.balance_due_date);
                              const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                              
                              if (daysUntilDue <= 0) {
                                return <Badge variant="destructive">Scaduto</Badge>;
                              } else if (daysUntilDue <= 3) {
                                return <Badge variant="destructive">Scade tra {daysUntilDue} giorni</Badge>;
                              } else if (daysUntilDue <= 7) {
                                return <Badge variant="outline" className="border-orange-300 text-orange-700">Scade tra {daysUntilDue} giorni</Badge>;
                              } else {
                                return <Badge variant="outline">Scade tra {daysUntilDue} giorni</Badge>;
                              }
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Riepilogo Finanziario</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Importo Lordo:</span>
                        <span className="font-semibold">{formatCurrency(adminData.gross_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Sconto:</span>
                        <span className="font-semibold text-red-600">-{formatCurrency(adminData.discount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Importo Netto:</span>
                        <span className="font-semibold">{formatCurrency(adminData.net_amount)}</span>
                      </div>
                      <hr className="border-slate-200" />
                      <div className="flex justify-between">
                        <span className="text-slate-600">Commissione Lorda:</span>
                        <span className="font-semibold">{formatCurrency(adminData.gross_commission)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Commissione Fornitore (4%):</span>
                        <span className="font-semibold">{formatCurrency(adminData.supplier_commission)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Commissione Agente:</span>
                        <span className="font-semibold text-blue-600">{formatCurrency(adminData.agent_commission)}</span>
                      </div>
                      <hr className="border-slate-200" />
                      <div className="flex justify-between">
                        <span className="text-slate-600">Totale Pagato:</span>
                        <span className="font-semibold text-green-600">{formatCurrency(adminData.total_paid || 0)}</span>
                      </div>
                      <div className="flex justify-between text-lg">
                        <span className="font-semibold">Saldo Dovuto:</span>
                        <span className="font-bold text-orange-600">{formatCurrency(adminData.balance_due)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Payments List in Overview */}
              {payments.length > 0 && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Storico Pagamenti ({payments.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-3 text-sm font-medium text-slate-600">Data</th>
                            <th className="text-left py-2 px-3 text-sm font-medium text-slate-600">Tipo</th>
                            <th className="text-left py-2 px-3 text-sm font-medium text-slate-600">Incassato da</th>
                            <th className="text-right py-2 px-3 text-sm font-medium text-slate-600">Importo</th>
                            <th className="text-left py-2 px-3 text-sm font-medium text-slate-600">Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((payment) => (
                            <tr key={payment.id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-3 text-sm">
                                {new Date(payment.payment_date).toLocaleDateString('it-IT', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </td>
                              <td className="py-3 px-3">
                                <Badge variant="outline" className="text-xs">
                                  {payment.payment_type === 'deposit' ? 'Acconto' : 
                                   payment.payment_type === 'balance' ? 'Saldo' : 'Rata'}
                                </Badge>
                              </td>
                              <td className="py-3 px-3">
                                <span className={`text-sm font-medium ${payment.collected_by === 'supplier' ? 'text-orange-600' : 'text-green-600'}`}>
                                  {payment.collected_by === 'supplier' ? 'Fornitore' : 'Agente'}
                                </span>
                              </td>
                              <td className="py-3 px-3 text-right">
                                <span className="font-semibold text-green-600">
                                  {formatCurrency(payment.amount)}
                                </span>
                              </td>
                              <td className="py-3 px-3 text-sm text-slate-500 max-w-[200px] truncate">
                                {payment.notes || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-slate-50">
                            <td colSpan="3" className="py-3 px-3 text-sm font-semibold text-slate-700">
                              Totale Pagamenti
                            </td>
                            <td className="py-3 px-3 text-right">
                              <span className="font-bold text-green-600">
                                {formatCurrency(payments.reduce((sum, p) => sum + (p.amount || 0), 0))}
                              </span>
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="payments">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-800">Gestione Pagamenti</h2>
                
                <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
                  <DialogTrigger asChild>
                    <Button className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Registra Pagamento
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Registra Nuovo Pagamento</DialogTitle>
                      <DialogDescription>
                        Inserisci i dettagli del pagamento ricevuto
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="amount">Importo (€)</Label>
                        <Input
                          id="amount"
                          type="number"
                          step="0.01"
                          value={newPayment.amount}
                          onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                          placeholder="500.00"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="payment_date">Data Pagamento</Label>
                        <Input
                          id="payment_date"
                          type="datetime-local"
                          value={newPayment.payment_date}
                          onChange={(e) => setNewPayment({...newPayment, payment_date: e.target.value})}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="payment_type">Tipo Pagamento</Label>
                        <Select value={newPayment.payment_type} onValueChange={(value) => setNewPayment({...newPayment, payment_type: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="deposit">Acconto</SelectItem>
                            <SelectItem value="installment">Rata</SelectItem>
                            <SelectItem value="balance">Saldo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="collected_by">Chi Incassa</Label>
                        <Select value={newPayment.collected_by} onValueChange={(value) => setNewPayment({...newPayment, collected_by: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="agent">Agente</SelectItem>
                            <SelectItem value="supplier">Fornitore</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="notes">Note (opzionale)</Label>
                        <Input
                          id="notes"
                          value={newPayment.notes}
                          onChange={(e) => setNewPayment({...newPayment, notes: e.target.value})}
                          placeholder="Bonifico bancario..."
                        />
                      </div>
                    </div>

                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
                        Annulla
                      </Button>
                      <Button onClick={handleCreatePayment}>
                        Registra Pagamento
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-4">
                {payments.length > 0 ? payments.map((payment) => (
                  <Card key={payment.id}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-full ${payment.collected_by === 'supplier' ? 'bg-orange-100' : 'bg-green-100'}`}>
                            <CreditCard className={`w-6 h-6 ${payment.collected_by === 'supplier' ? 'text-orange-600' : 'text-green-600'}`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">
                              Pagamento {payment.payment_type === 'deposit' ? 'Acconto' : payment.payment_type === 'balance' ? 'Saldo' : 'Rata'}
                            </h4>
                            <p className="text-sm text-slate-600">
                              {new Date(payment.payment_date).toLocaleDateString('it-IT')}
                            </p>
                            <p className="text-sm text-slate-500">
                              Incassato da: <span className={`font-medium ${payment.collected_by === 'supplier' ? 'text-orange-600' : 'text-green-600'}`}>
                                {getCollectedByLabel(payment.collected_by)}
                              </span>
                            </p>
                            {payment.notes && (
                              <p className="text-sm text-slate-400 italic">{payment.notes}</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${payment.collected_by === 'supplier' ? 'text-orange-600' : 'text-green-600'}`}>
                              {formatCurrency(payment.amount)}
                            </div>
                            <div className="flex gap-1 justify-end">
                              <Badge variant="outline">
                                {payment.payment_type === 'deposit' ? 'Acconto' : payment.payment_type === 'balance' ? 'Saldo' : 'Rata'}
                              </Badge>
                              <Badge variant={payment.collected_by === 'supplier' ? 'secondary' : 'default'} className="text-xs">
                                {getCollectedByLabel(payment.collected_by)}
                              </Badge>
                            </div>
                          </div>
                          
                          <div className="flex flex-col gap-1">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleEditPayment(payment)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDeletePayment(payment.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )) : (
                  <Card>
                    <CardContent className="text-center py-16">
                      <CreditCard className="mx-auto h-16 w-16 text-slate-400 mb-6" />
                      <h3 className="text-lg font-medium text-slate-800 mb-2">
                        Nessun pagamento registrato
                      </h3>
                      <p className="text-slate-600 mb-6">
                        Inizia registrando il primo pagamento ricevuto dal cliente
                      </p>
                      <Button onClick={() => setShowPaymentDialog(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Registra primo pagamento
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Supplier Costs Tab - Costi Tour Operator */}
            <TabsContent value="supplier-costs">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-800">Costi Tour Operator / Fornitori</h2>
                
                <Dialog open={showSupplierCostDialog} onOpenChange={setShowSupplierCostDialog}>
                  <DialogTrigger asChild>
                    <Button className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Aggiungi Costo
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Registra Costo Fornitore</DialogTitle>
                      <DialogDescription>
                        Inserisci i dettagli del costo pagato al Tour Operator
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      <TourOperatorSelect
                        value={newSupplierCost.tour_operator_id}
                        onChange={(id) => setNewSupplierCost({...newSupplierCost, tour_operator_id: id})}
                        onSelect={(op) => {
                          if (op) {
                            setNewSupplierCost({
                              ...newSupplierCost,
                              tour_operator_id: op.id,
                              supplier_name: op.name,
                            });
                          }
                        }}
                        label="Tour Operator (da Rubrica)"
                        testId="cost-tour-operator-select"
                      />

                      <div className="space-y-2">
                        <Label>Tour Operator / Fornitore *</Label>
                        <Input
                          value={newSupplierCost.supplier_name}
                          onChange={(e) => setNewSupplierCost({...newSupplierCost, supplier_name: e.target.value})}
                          placeholder="Es. Alpitour, Eden Viaggi..."
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Descrizione</Label>
                        <Input
                          value={newSupplierCost.description}
                          onChange={(e) => setNewSupplierCost({...newSupplierCost, description: e.target.value})}
                          placeholder="Es. Hotel 5 notti, Volo A/R..."
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Importo (€) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newSupplierCost.amount}
                            onChange={(e) => setNewSupplierCost({...newSupplierCost, amount: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Data *</Label>
                          <Input
                            type="datetime-local"
                            value={newSupplierCost.cost_date}
                            onChange={(e) => setNewSupplierCost({...newSupplierCost, cost_date: e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Categoria</Label>
                        <Select value={newSupplierCost.category} onValueChange={(value) => setNewSupplierCost({...newSupplierCost, category: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="accommodation">Alloggio</SelectItem>
                            <SelectItem value="transport">Trasporto</SelectItem>
                            <SelectItem value="excursion">Escursione</SelectItem>
                            <SelectItem value="insurance">Assicurazione</SelectItem>
                            <SelectItem value="other">Altro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Note</Label>
                        <Input
                          value={newSupplierCost.notes}
                          onChange={(e) => setNewSupplierCost({...newSupplierCost, notes: e.target.value})}
                        />
                      </div>
                    </div>

                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowSupplierCostDialog(false)}>Annulla</Button>
                      <Button onClick={handleCreateSupplierCost}>Registra Costo</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-4">
                {supplierCosts.length > 0 ? supplierCosts.map((cost) => (
                  <Card key={cost.id}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-orange-100 rounded-full">
                            <Building className="w-6 h-6 text-orange-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">{cost.supplier_name}</h4>
                            <p className="text-sm text-slate-600">{cost.description}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(cost.cost_date).toLocaleDateString('it-IT')} • {getCostCategoryLabel(cost.category)}
                            </p>
                            {cost.notes && <p className="text-xs text-slate-400 italic">{cost.notes}</p>}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-2xl font-bold text-orange-600">{formatCurrency(cost.amount)}</div>
                            <Badge variant="outline">{getCostCategoryLabel(cost.category)}</Badge>
                          </div>
                          
                          <div className="flex flex-col gap-1">
                            <Button size="sm" variant="outline" onClick={() => handleEditSupplierCost(cost)} className="text-blue-600">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDeleteSupplierCost(cost.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )) : (
                  <Card>
                    <CardContent className="text-center py-16">
                      <Building className="mx-auto h-16 w-16 text-slate-400 mb-6" />
                      <h3 className="text-lg font-medium text-slate-800 mb-2">Nessun costo registrato</h3>
                      <p className="text-slate-600 mb-6">Registra i costi pagati ai Tour Operator e fornitori</p>
                      <Button onClick={() => setShowSupplierCostDialog(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Aggiungi primo costo
                      </Button>
                    </CardContent>
                  </Card>
                )}
                
                {supplierCosts.length > 0 && (
                  <Card className="bg-orange-50">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-slate-700">Totale Costi Fornitori</span>
                        <span className="text-2xl font-bold text-orange-600">
                          {formatCurrency(supplierCosts.reduce((sum, c) => sum + (c.amount || 0), 0))}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Supplier Payments Tab - Bonifici ai Fornitori */}
            <TabsContent value="supplier-payments">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-800">Bonifici ai Fornitori</h2>
                
                <Dialog open={showSupplierPaymentDialog} onOpenChange={setShowSupplierPaymentDialog}>
                  <DialogTrigger asChild>
                    <Button className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Registra Bonifico
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Registra Bonifico al Fornitore</DialogTitle>
                      <DialogDescription>
                        Inserisci i dettagli del bonifico inviato al Tour Operator
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                      <TourOperatorSelect
                        value={newSupplierPayment.tour_operator_id}
                        onChange={(id) => setNewSupplierPayment({...newSupplierPayment, tour_operator_id: id})}
                        onSelect={(op) => {
                          if (op) {
                            setNewSupplierPayment({
                              ...newSupplierPayment,
                              tour_operator_id: op.id,
                              supplier_name: op.name,
                            });
                          }
                        }}
                        label="Tour Operator (da Rubrica)"
                        testId="transfer-tour-operator-select"
                      />

                      <div className="space-y-2">
                        <Label>Tour Operator / Fornitore *</Label>
                        <Input
                          value={newSupplierPayment.supplier_name}
                          onChange={(e) => setNewSupplierPayment({...newSupplierPayment, supplier_name: e.target.value})}
                          placeholder="Es. Alpitour, Eden Viaggi..."
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Importo (€) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newSupplierPayment.amount}
                            onChange={(e) => setNewSupplierPayment({...newSupplierPayment, amount: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Data Bonifico *</Label>
                          <Input
                            type="datetime-local"
                            value={newSupplierPayment.payment_date}
                            onChange={(e) => setNewSupplierPayment({...newSupplierPayment, payment_date: e.target.value})}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Riferimento/CRO Bonifico</Label>
                        <Input
                          value={newSupplierPayment.bank_reference}
                          onChange={(e) => setNewSupplierPayment({...newSupplierPayment, bank_reference: e.target.value})}
                          placeholder="Es. CRO123456789"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Metodo di Pagamento</Label>
                        <Select value={newSupplierPayment.payment_method} onValueChange={(value) => setNewSupplierPayment({...newSupplierPayment, payment_method: value})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bank_transfer">Bonifico Bancario</SelectItem>
                            <SelectItem value="credit_card">Carta di Credito</SelectItem>
                            <SelectItem value="other">Altro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Note</Label>
                        <Input
                          value={newSupplierPayment.notes}
                          onChange={(e) => setNewSupplierPayment({...newSupplierPayment, notes: e.target.value})}
                        />
                      </div>
                    </div>

                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowSupplierPaymentDialog(false)}>Annulla</Button>
                      <Button onClick={handleCreateSupplierPayment}>Registra Bonifico</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-4">
                {supplierPayments.length > 0 ? supplierPayments.map((payment) => (
                  <Card key={payment.id}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-blue-100 rounded-full">
                            <Send className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800">{payment.supplier_name}</h4>
                            <p className="text-sm text-slate-600">
                              {new Date(payment.payment_date).toLocaleDateString('it-IT')}
                            </p>
                            {payment.bank_reference && (
                              <p className="text-xs text-slate-500">Rif: {payment.bank_reference}</p>
                            )}
                            {payment.notes && <p className="text-xs text-slate-400 italic">{payment.notes}</p>}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-2xl font-bold text-blue-600">{formatCurrency(payment.amount)}</div>
                            <Badge variant="outline">
                              {payment.payment_method === 'bank_transfer' ? 'Bonifico' : 
                               payment.payment_method === 'credit_card' ? 'Carta' : 'Altro'}
                            </Badge>
                          </div>
                          
                          <div className="flex flex-col gap-1">
                            <Button size="sm" variant="outline" onClick={() => handleEditSupplierPayment(payment)} className="text-blue-600">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDeleteSupplierPayment(payment.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )) : (
                  <Card>
                    <CardContent className="text-center py-16">
                      <Send className="mx-auto h-16 w-16 text-slate-400 mb-6" />
                      <h3 className="text-lg font-medium text-slate-800 mb-2">Nessun bonifico registrato</h3>
                      <p className="text-slate-600 mb-6">Registra i bonifici inviati ai Tour Operator</p>
                      <Button onClick={() => setShowSupplierPaymentDialog(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Registra primo bonifico
                      </Button>
                    </CardContent>
                  </Card>
                )}
                
                {supplierPayments.length > 0 && (
                  <Card className="bg-blue-50">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-slate-700">Totale Bonifici Inviati</span>
                        <span className="text-2xl font-bold text-blue-600">
                          {formatCurrency(supplierPayments.reduce((sum, p) => sum + (p.amount || 0), 0))}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="analytics">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Analisi Finanziaria
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-4">Stato Pagamenti</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <span className="text-green-800">Pagato</span>
                          <span className="font-semibold text-green-800">
                            {formatCurrency(adminData.gross_amount - adminData.balance_due)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                          <span className="text-orange-800">Da pagare</span>
                          <span className="font-semibold text-orange-800">
                            {formatCurrency(adminData.balance_due)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-slate-800 mb-4">Commissioni</h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <span className="text-blue-800">Commissione Agente</span>
                          <span className="font-semibold text-blue-800">
                            {formatCurrency(adminData.agent_commission)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                          <span className="text-purple-800">Commissione Fornitore</span>
                          <span className="font-semibold text-purple-800">
                            {formatCurrency(adminData.supplier_commission)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {(adminData.balance_due > 0 || adminData.balance_due_date) && (
                    <div className="mt-8 space-y-4">
                      {adminData.balance_due > 0 && (
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <div className="flex items-center gap-3">
                            <AlertCircle className="w-5 h-5 text-yellow-600" />
                            <div>
                              <h4 className="font-medium text-yellow-800">Attenzione</h4>
                              <p className="text-sm text-yellow-700">
                                È presente un saldo dovuto di {formatCurrency(adminData.balance_due)}. 
                                Assicurati che il cliente effettui il pagamento prima della partenza.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {adminData.balance_due_date && (() => {
                        const today = new Date();
                        const dueDate = new Date(adminData.balance_due_date);
                        const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                        
                        if (daysUntilDue <= 3) {
                          return (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                              <div className="flex items-center gap-3">
                                <AlertCircle className="w-5 h-5 text-red-600" />
                                <div>
                                  <h4 className="font-medium text-red-800">
                                    {daysUntilDue <= 0 ? 'Saldo Scaduto!' : 'Scadenza Saldo Imminente!'}
                                  </h4>
                                  <p className="text-sm text-red-700">
                                    {daysUntilDue <= 0 
                                      ? `Il saldo è scaduto il ${dueDate.toLocaleDateString('it-IT')}. Contatta urgentemente il cliente.`
                                      : `Il saldo scade ${daysUntilDue === 1 ? 'domani' : `tra ${daysUntilDue} giorni`} (${dueDate.toLocaleDateString('it-IT')}). Ricorda al cliente di effettuare il pagamento.`
                                    }
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        } else if (daysUntilDue <= 7) {
                          return (
                            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                              <div className="flex items-center gap-3">
                                <Calendar className="w-5 h-5 text-orange-600" />
                                <div>
                                  <h4 className="font-medium text-orange-800">Promemoria Scadenza Saldo</h4>
                                  <p className="text-sm text-orange-700">
                                    Il saldo scade tra {daysUntilDue} giorni ({dueDate.toLocaleDateString('it-IT')}). 
                                    Considera di inviare un promemoria al cliente.
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          <Card>
            <CardContent className="text-center py-16">
              <DollarSign className="mx-auto h-16 w-16 text-slate-400 mb-6" />
              <h3 className="text-lg font-medium text-slate-800 mb-2">
                Nessuna scheda finanziaria
              </h3>
              <p className="text-slate-600 mb-6">
                Crea una scheda finanziaria per gestire i dati amministrativi di questo viaggio
              </p>
              <Button onClick={() => setShowCreateAdminDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crea scheda finanziaria
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Edit Payment Dialog */}
      <Dialog open={showEditPaymentDialog} onOpenChange={setShowEditPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica Pagamento</DialogTitle>
            <DialogDescription>
              Modifica i dettagli del pagamento
            </DialogDescription>
          </DialogHeader>
          
          {editingPayment && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_amount">Importo (€)</Label>
                <Input
                  id="edit_amount"
                  type="number"
                  step="0.01"
                  value={editingPayment.amount}
                  onChange={(e) => setEditingPayment({...editingPayment, amount: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_payment_date">Data Pagamento</Label>
                <Input
                  id="edit_payment_date"
                  type="datetime-local"
                  value={editingPayment.payment_date}
                  onChange={(e) => setEditingPayment({...editingPayment, payment_date: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_payment_type">Tipo Pagamento</Label>
                <Select value={editingPayment.payment_type} onValueChange={(value) => setEditingPayment({...editingPayment, payment_type: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deposit">Acconto</SelectItem>
                    <SelectItem value="installment">Rata</SelectItem>
                    <SelectItem value="balance">Saldo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_collected_by">Chi Incassa</Label>
                <Select value={editingPayment.collected_by || 'agent'} onValueChange={(value) => setEditingPayment({...editingPayment, collected_by: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agent">Agente</SelectItem>
                    <SelectItem value="supplier">Fornitore</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_notes">Note (opzionale)</Label>
                <Input
                  id="edit_notes"
                  value={editingPayment.notes || ''}
                  onChange={(e) => setEditingPayment({...editingPayment, notes: e.target.value})}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {setShowEditPaymentDialog(false); setEditingPayment(null);}}>
              Annulla
            </Button>
            <Button onClick={handleUpdatePayment}>
              Salva Modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Supplier Cost Dialog */}
      <Dialog open={showEditSupplierCostDialog} onOpenChange={setShowEditSupplierCostDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica Costo Fornitore</DialogTitle>
          </DialogHeader>
          
          {editingSupplierCost && (
            <div className="space-y-4">
              <TourOperatorSelect
                value={editingSupplierCost.tour_operator_id}
                onChange={(id) => setEditingSupplierCost({...editingSupplierCost, tour_operator_id: id})}
                onSelect={(op) => {
                  if (op) {
                    setEditingSupplierCost({
                      ...editingSupplierCost,
                      tour_operator_id: op.id,
                      supplier_name: op.name,
                    });
                  }
                }}
                label="Tour Operator (da Rubrica)"
                testId="edit-cost-tour-operator-select"
              />
              <div className="space-y-2">
                <Label>Tour Operator / Fornitore</Label>
                <Input
                  value={editingSupplierCost.supplier_name}
                  onChange={(e) => setEditingSupplierCost({...editingSupplierCost, supplier_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Input
                  value={editingSupplierCost.description || ''}
                  onChange={(e) => setEditingSupplierCost({...editingSupplierCost, description: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Importo (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingSupplierCost.amount}
                    onChange={(e) => setEditingSupplierCost({...editingSupplierCost, amount: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data</Label>
                  <Input
                    type="datetime-local"
                    value={editingSupplierCost.cost_date}
                    onChange={(e) => setEditingSupplierCost({...editingSupplierCost, cost_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={editingSupplierCost.category} onValueChange={(value) => setEditingSupplierCost({...editingSupplierCost, category: value})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accommodation">Alloggio</SelectItem>
                    <SelectItem value="transport">Trasporto</SelectItem>
                    <SelectItem value="excursion">Escursione</SelectItem>
                    <SelectItem value="insurance">Assicurazione</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Input
                  value={editingSupplierCost.notes || ''}
                  onChange={(e) => setEditingSupplierCost({...editingSupplierCost, notes: e.target.value})}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {setShowEditSupplierCostDialog(false); setEditingSupplierCost(null);}}>Annulla</Button>
            <Button onClick={handleUpdateSupplierCost}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Supplier Payment Dialog */}
      <Dialog open={showEditSupplierPaymentDialog} onOpenChange={setShowEditSupplierPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica Bonifico</DialogTitle>
          </DialogHeader>
          
          {editingSupplierPayment && (
            <div className="space-y-4">
              <TourOperatorSelect
                value={editingSupplierPayment.tour_operator_id}
                onChange={(id) => setEditingSupplierPayment({...editingSupplierPayment, tour_operator_id: id})}
                onSelect={(op) => {
                  if (op) {
                    setEditingSupplierPayment({
                      ...editingSupplierPayment,
                      tour_operator_id: op.id,
                      supplier_name: op.name,
                    });
                  }
                }}
                label="Tour Operator (da Rubrica)"
                testId="edit-transfer-tour-operator-select"
              />
              <div className="space-y-2">
                <Label>Tour Operator / Fornitore</Label>
                <Input
                  value={editingSupplierPayment.supplier_name}
                  onChange={(e) => setEditingSupplierPayment({...editingSupplierPayment, supplier_name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Importo (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingSupplierPayment.amount}
                    onChange={(e) => setEditingSupplierPayment({...editingSupplierPayment, amount: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data Bonifico</Label>
                  <Input
                    type="datetime-local"
                    value={editingSupplierPayment.payment_date}
                    onChange={(e) => setEditingSupplierPayment({...editingSupplierPayment, payment_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Riferimento/CRO</Label>
                <Input
                  value={editingSupplierPayment.bank_reference || ''}
                  onChange={(e) => setEditingSupplierPayment({...editingSupplierPayment, bank_reference: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Metodo di Pagamento</Label>
                <Select value={editingSupplierPayment.payment_method} onValueChange={(value) => setEditingSupplierPayment({...editingSupplierPayment, payment_method: value})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bonifico Bancario</SelectItem>
                    <SelectItem value="credit_card">Carta di Credito</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Input
                  value={editingSupplierPayment.notes || ''}
                  onChange={(e) => setEditingSupplierPayment({...editingSupplierPayment, notes: e.target.value})}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {setShowEditSupplierPaymentDialog(false); setEditingSupplierPayment(null);}}>Annulla</Button>
            <Button onClick={handleUpdateSupplierPayment}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TripAdmin;